<template>
  <Teleport to="body">
    <div v-if="open" class="auth-overlay">
      <section class="auth-card" role="dialog" aria-modal="true">
        <header class="auth-head">
          <div>
            <span class="auth-brand">KomeiC</span>
            <h2>{{ mode === "login" ? "欢迎回来" : "创建账号" }}</h2>
          </div>
          <button class="auth-close" type="button" aria-label="关闭" @click="close">
            <icon-close />
          </button>
        </header>

        <div class="auth-tabs">
          <button
            type="button"
            :class="{ on: mode === 'login' }"
            @click="setMode('login')"
          >
            登录
          </button>
          <button
            type="button"
            :class="{ on: mode === 'register' }"
            @click="setMode('register')"
          >
            注册
          </button>
        </div>

        <form v-if="mode === 'login'" class="auth-form" @submit.prevent="submitLogin">
          <label>
            <span>账号或邮箱</span>
            <a-input v-model="loginForm.userAccount" placeholder="邮箱或用户名" autocomplete="username" size="large">
              <template #prefix><icon-user /></template>
            </a-input>
          </label>
          <label>
            <span>密码</span>
            <a-input-password v-model="loginForm.userPassword" placeholder="请输入密码" autocomplete="current-password" size="large">
              <template #prefix><icon-lock /></template>
            </a-input-password>
          </label>
          <button class="forgot-link" type="button" @click="openPasswordReset">忘记密码？</button>
          <a-button
            type="primary"
            html-type="submit"
            long
            :loading="submitting"
            class="auth-submit"
          >
            登录
          </a-button>
        </form>

        <form v-else class="auth-form" @submit.prevent="submitRegister">
          <label>
            <span>邮箱</span>
            <a-input v-model="regForm.email" placeholder="name@example.com" autocomplete="email" size="large">
              <template #prefix><icon-user /></template>
            </a-input>
          </label>
          <label>
            <span>设置密码</span>
            <a-input-password v-model="regForm.userPassword" placeholder="8 位以上" autocomplete="new-password" size="large">
              <template #prefix><icon-lock /></template>
            </a-input-password>
          </label>
          <label>
            <span>确认密码</span>
            <a-input-password v-model="regForm.checkPassword" placeholder="请再次输入" autocomplete="new-password" size="large">
              <template #prefix><icon-safe /></template>
            </a-input-password>
          </label>
          <label>
            <span>邮箱验证码</span>
            <div class="captcha-row">
              <a-input v-model="regForm.emailCode" placeholder="4 位验证码" :max-length="4" autocomplete="one-time-code" size="large" />
              <a-button
                class="code-button"
                :disabled="countdown > 0"
                :loading="sending"
                @click.prevent="sendCode"
              >
                {{ countdown > 0 ? `${countdown} 秒后重发` : "发送验证码" }}
              </a-button>
            </div>
          </label>
          <label>
            <span>图形验证码</span>
            <div class="captcha-row">
              <a-input
                v-model="captchaCode"
                placeholder="4 位字符"
                :max-length="4"
                autocomplete="off"
                size="large"
              />
              <img
                v-if="captchaImage"
                :src="captchaImage"
                alt="图形验证码，点击可刷新"
                class="captcha-thumb"
                title="点击刷新"
                @click="loadCaptcha"
              />
            </div>
          </label>
          <a-button
            type="primary"
            html-type="submit"
            long
            :loading="submitting"
            class="auth-submit"
          >
            立即注册
          </a-button>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { IconClose, IconLock, IconSafe, IconUser } from "@arco-design/web-vue/es/icon";
import { getEmailCodeCaptcha, registerByEmail, sendRegistrationEmailCode, userLoginUsingPost } from "@/api/userController";
import { useLoginUserStore } from "@/store/userStore";
import { useAuthModal } from "@/composables/useAuthModal";
import message from "@arco-design/web-vue/es/message";

const router = useRouter();
const { open, mode, returnTo, close, setMode } = useAuthModal();
const loginUserStore = useLoginUserStore();
const submitting = ref(false);
const sending = ref(false);
const countdown = ref(0);
const captchaCode = ref("");
const captchaImage = ref("");
const loginForm = ref({ userAccount: "", userPassword: "" });
const regForm = ref({ email: "", userPassword: "", checkPassword: "", emailCode: "" });
const openPasswordReset = () => { close(); router.push("/user/login?reset=1"); };
let timer: number | undefined;

const loadCaptcha = async () => {
  captchaCode.value = "";
  try {
    const r = await getEmailCodeCaptcha();
    if (r.data.code === 0) captchaImage.value = String(r.data.data || "");
  } catch {
    message.error("图形验证码加载失败");
  }
};

watch(mode, () => {
  loginForm.value = { userAccount: "", userPassword: "" };
  regForm.value = { email: "", userPassword: "", checkPassword: "", emailCode: "" };
  if (mode.value === "register") loadCaptcha();
}, { immediate: true });

const sendCode = async () => {
  if (!regForm.value.email) return message.warning("请先输入邮箱");
  if (!captchaCode.value || captchaCode.value.trim().length !== 4)
    return message.warning("请输入 4 位图形验证码");
  const captcha = captchaCode.value.trim();
  sending.value = true;
  try {
    const r = await sendRegistrationEmailCode(regForm.value.email, captcha);
    if (r.data.code !== 0) throw new Error(r.data.message);
    message.success("验证码已发送");
    countdown.value = 60;
    loadCaptcha();
    timer = window.setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0 && timer) window.clearInterval(timer);
    }, 1000);
  } catch (e: any) {
    message.error(e?.response?.data?.message || e?.message || "发送失败");
    loadCaptcha();
  } finally {
    sending.value = false;
  }
};

const submitLogin = async () => {
  if (!loginForm.value.userAccount || !loginForm.value.userPassword)
    return message.error("请输入账号和密码");
  submitting.value = true;
  try {
    const res = await userLoginUsingPost(loginForm.value);
    if (res.data.code === 0) {
      await loginUserStore.fetchLoginUser();
      message.success("登录成功");
      close();
      router.push(returnTo.value);
    } else {
      message.error(res.data.message);
    }
  } catch {
    message.error("登录失败，请重试");
  } finally {
    submitting.value = false;
  }
};

const submitRegister = async () => {
  const f = regForm.value;
  if (!f.email || !f.userPassword || !f.checkPassword || !f.emailCode)
    return message.error("请填写完整信息");
  if (f.userPassword !== f.checkPassword) return message.error("两次密码不一致");
  submitting.value = true;
  try {
    const res = await registerByEmail(f);
    if (res.data.code === 0) {
      try {
        const login = await userLoginUsingPost({ userAccount: f.email, userPassword: f.userPassword });
        if (login.data.code !== 0) throw new Error(login.data.message);
        await loginUserStore.fetchLoginUser();
        message.success("注册并登录成功");
        close();
        router.push(returnTo.value);
      } catch {
        message.warning("账户已创建，请登录");
        setMode("login");
        loginForm.value.userAccount = f.email;
      }
    } else {
      message.error(res.data.message || "注册失败");
    }
  } catch {
    message.error("注册失败");
  } finally {
    submitting.value = false;
  }
};

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer);
});
</script>

<style scoped>
.auth-overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.78);
  backdrop-filter: blur(8px);
}
.auth-card {
  width: min(400px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  padding: 26px 26px 24px;
  border: 1px solid var(--km-line-strong);
  border-radius: 8px;
  background: #101215;
  color: #f5f5f5;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.5);
}
.auth-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}
.auth-head h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 650;
}
.auth-brand{display:block;margin-bottom:18px;color:#fff;font-size:13px;font-weight:650}
.auth-head p {
  margin: 0;
  color: rgba(255,255,255,.5);
  font-size: 13px;
  line-height: 1.6;
}
.auth-close {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: rgba(255,255,255,.55);
  cursor: pointer;
}
.auth-close:hover {
  background: rgba(255,255,255,.08);
  color: #fff;
}
.auth-tabs {
  display: flex;
  gap: 6px;
  margin: 20px 0 22px;
  padding: 4px;
  border: 1px solid rgba(16, 20, 26, 0.12);
  border-radius: 5px;
  background: #17191d;
}
.auth-tabs button {
  flex: 1;
  min-height: 38px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: rgba(255,255,255,.5);
  font-weight: 600;
  cursor: pointer;
}
.auth-tabs button.on {
  background: #f5f5f5;
  color: #0b0c0e;
}
.auth-form {
  display: grid;
  gap: 16px;
}
.auth-form label {
  display: grid;
  gap: 8px;
  color: rgba(255,255,255,.66);
  font-size: 13px;
}
.auth-form :deep(.arco-input-wrapper) {
  min-height: 46px;
  border-radius: 5px;
  background: #181a1e !important;
  border-color: rgba(255,255,255,.16) !important;
}
.auth-form :deep(.arco-input),
.auth-form :deep(.arco-textarea) {
  color: #f5f5f5 !important;
}
.auth-form :deep(.arco-input::placeholder),
.auth-form :deep(.arco-textarea::placeholder) {
  color: rgba(255,255,255,.4) !important;
}
.auth-form :deep(input:-webkit-autofill),
.auth-form :deep(input:autofill) {
  -webkit-text-fill-color: #f5f5f5 !important;
  caret-color: #f5f5f5;
  -webkit-box-shadow: 0 0 0 1000px #181a1e inset !important;
  box-shadow: 0 0 0 1000px #181a1e inset !important;
  transition: background-color 9999s ease-out;
}
.captcha-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 138px;
  gap: 8px;
}
.captcha-thumb{width:138px;height:46px;object-fit:contain;border-radius:5px;background:#fff;cursor:pointer}
.code-button{min-height:46px!important;border-color:rgba(255,255,255,.28)!important;background:transparent!important;color:#fff!important}.forgot-link{justify-self:end;min-height:36px;margin-top:-8px;margin-bottom:-2px;border:0;background:transparent;color:rgba(255,255,255,.72);cursor:pointer}.forgot-link:hover{color:#fff}
.auth-submit {
  min-height: 48px;
  border-radius: 5px;
  background:#f5f5f5!important;
  border-color:#f5f5f5!important;
  color:#0b0c0e!important;
  font-weight: 700;
}
@media(max-width:480px){.auth-overlay{padding:0;align-items:stretch}.auth-card{width:100%;max-height:100dvh;min-height:100dvh;border:0;border-radius:0;padding:calc(24px + env(safe-area-inset-top)) 20px calc(24px + env(safe-area-inset-bottom))}.captcha-row{grid-template-columns:minmax(0,1fr) 126px}}
</style>
